/**
 * LGPD Block E — Google Analytics gating + payload sanitization (PURE).
 *
 * Zero imports / no DOM / no `import.meta.env` access, so this module is unit-
 * testable in plain Node (see scripts/verify-analytics-readiness.mjs). The
 * env-/DOM-aware wrapper lives in `ga.ts` and consumes these helpers.
 *
 * Hard rule: GA loads ONLY when explicitly enabled by env AND a measurement id
 * is present AND the user granted analytics consent. Missing any → never load.
 */

export interface GaConfig {
  enabled: boolean
  measurementId: string
}

/** GA4 gtag.js base URL (only used by the consent-gated loader in ga.ts). */
export const GA_SCRIPT_BASE = 'https://www.googletagmanager.com/gtag/js'

/**
 * The single source of truth for "may GA load right now?". All five roadmap
 * conditions collapse to this:
 *   - env disabled                 → false
 *   - no measurement id            → false
 *   - no analytics consent         → false (covers no-consent & reject_all)
 *   - analytics=false              → false
 *   - enabled + id + analytics=true → true
 */
export function gaShouldLoad(
  config: GaConfig,
  hasAnalyticsConsent: boolean,
): boolean {
  if (!config.enabled) return false
  if (!config.measurementId || config.measurementId.trim().length === 0) {
    return false
  }
  if (hasAnalyticsConsent !== true) return false
  return true
}

/** Build the gtag.js URL for a measurement id (id is URL-encoded). */
export function gaScriptUrl(measurementId: string): string {
  return `${GA_SCRIPT_BASE}?id=${encodeURIComponent(measurementId)}`
}

// GA4 event names: start with a letter, then letters/digits/underscores.
const SAFE_EVENT_NAME = /^[a-zA-Z][a-zA-Z0-9_]{0,39}$/

export function isSafeEventName(name: unknown): boolean {
  return typeof name === 'string' && SAFE_EVENT_NAME.test(name)
}

// Param KEY must be a short, safe identifier…
const SAFE_PARAM_KEY = /^[a-zA-Z0-9_]{1,40}$/
// …and must NOT look like it could carry personal data / content.
const FORBIDDEN_PARAM_KEY =
  /(email|name|prompt|question|query|message|content|document|file|user|token|secret|password|cpf|cnpj|phone|address|ip)/i

/**
 * Allow only safe, non-PII scalar params. Drops unknown/unsafe keys, objects,
 * functions, emails, and over-long strings. Returns a new object — never
 * mutates the input.
 */
export function sanitizeGaParams(
  params?: Record<string, unknown>,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {}
  if (!params || typeof params !== 'object') return out
  for (const [key, value] of Object.entries(params)) {
    if (!SAFE_PARAM_KEY.test(key)) continue
    if (FORBIDDEN_PARAM_KEY.test(key)) continue
    if (typeof value === 'number' && Number.isFinite(value)) {
      out[key] = value
    } else if (typeof value === 'boolean') {
      out[key] = value
    } else if (typeof value === 'string') {
      if (value.length > 100) continue
      if (value.includes('@')) continue // never forward anything email-like
      out[key] = value
    }
    // objects / arrays / functions / null / undefined are dropped
  }
  return out
}

/** Normalize a page path: drop any query string / fragment (may carry PII). */
export function sanitizePagePath(path?: string): string | undefined {
  if (typeof path !== 'string' || path.length === 0) return undefined
  return path.split('?')[0].split('#')[0].slice(0, 200)
}
